import { Card, CardBody, CardTitle } from '@fold-and-flow/ui'
import { Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { useSession } from '#lib/auth'
import { trpc } from '#lib/trpc'

export function DashboardPage() {
  const { data: session } = useSession()
  const { data: profile } = trpc.profile.get.useQuery()
  const { data: recentSessions } = trpc.session.recent.useQuery({ limit: 5 })

  const name = session?.user.name.split(' ')[0] ?? 'there'
  const now = new Date()
  const hour = now.getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'
  const today = now.toLocaleDateString('en-US', { weekday: 'long' })

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="flex flex-col gap-8"
    >
      <div>
        <h1 className="font-serif text-3xl text-warm-gray-900">
          {greeting}, {name}
        </h1>
        <p className="text-warm-gray-400 mt-1">{today} — How are you today?</p>
      </div>

      {!profile && (
        <Card elevated>
          <CardBody>
            <CardTitle className="mb-2">Welcome to Fold & Flow</CardTitle>
            <p className="text-sm text-warm-gray-600 mb-5">
              Tell us a bit about your practice so we can personalize your flows.
            </p>
            <Link
              to="/onboarding"
              className="inline-flex items-center justify-center h-12 px-7 rounded-2xl bg-terracotta-500 text-white text-base font-medium shadow-warm-sm hover:bg-terracotta-600 transition-colors"
            >
              Set up my profile →
            </Link>
          </CardBody>
        </Card>
      )}

      {profile && (
        <Card elevated className="bg-gradient-to-br from-white to-cream-100">
          <CardBody>
            <CardTitle className="mb-1">Start Today's Practice</CardTitle>
            <p className="text-sm text-warm-gray-400 mb-6">
              Answer a few quick questions and we'll build a flow just for you.
            </p>
            <Link
              to="/checkin"
              className="inline-flex items-center justify-center h-14 px-9 rounded-2xl bg-terracotta-500 text-white text-lg font-medium shadow-warm-sm hover:bg-terracotta-600 transition-colors"
            >
              Begin check-in →
            </Link>
          </CardBody>
        </Card>
      )}

      {recentSessions && recentSessions.length > 0 && (
        <div>
          <h2 className="font-serif text-lg text-warm-gray-900 mb-4">Recent Sessions</h2>
          <div className="flex flex-col gap-3">
            {recentSessions.map((s) => {
              const date = new Date(s.startedAt).toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              })
              const pct = Math.round(s.completionRate * 100)
              return (
                <Link
                  key={s.id}
                  to="/journal/$sessionId"
                  params={{ sessionId: s.id }}
                  className="flex items-center gap-4 px-5 py-4 bg-white rounded-2xl border border-cream-200 hover:border-warm-gray-400 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-warm-gray-800">{date}</p>
                    <p className="text-xs text-warm-gray-400 mt-0.5">{pct}% complete</p>
                  </div>
                  <div
                    className="h-1.5 w-24 rounded-full bg-cream-200 overflow-hidden flex-shrink-0"
                    role="progressbar"
                    tabIndex={0}
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div className="h-full rounded-full bg-sage-400" style={{ width: `${pct}%` }} />
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      )}
    </motion.div>
  )
}
